import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 0, 1, 2, 3, 4];
const fmt = (h, m = 0) => `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
const TIMES = HOURS.flatMap((h) => [fmt(h), fmt(h, 30)]);

const toDate = (date, time) => {
  const [hm, ap] = time.trim().toUpperCase().split(' ');
  let [h, m] = hm.split(':').map(Number);
  if (ap === 'PM' && h !== 12) h += 12;
  if (ap === 'AM' && h === 12) h = 0;
  const [y, mo, d] = date.split('-').map(Number);
  return new Date(y, mo - 1, d, h, m || 0);
};
const ymd = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;

// One-screen "Move session": quick +30m/+1h/+2h or any date/time, notifies client + engineer.
export const MoveSessionModal = ({ session, onClose, onMoved }) => {
  const [date, setDate] = useState(session.date);
  const [time, setTime] = useState(session.time);
  const [saving, setSaving] = useState(false);

  const shift = (mins) => {
    const dt = new Date(toDate(session.date, session.time).getTime() + mins * 60000);
    setDate(ymd(dt));
    setTime(fmt(dt.getHours(), dt.getMinutes()));
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/bookings/${session.id}/manual-reschedule`, {
        new_date: date, new_time: time, send_confirmation: true, notify_sms: true,
      });
      toast.success(`Moved to ${date} at ${time}${r.data.email_sent ? ' · email sent' : ''}${r.data.texts_sent ? ` · ${r.data.texts_sent} text(s) sent` : ''}`);
      onMoved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not move session');
    } finally {
      setSaving(false);
    }
  };

  const changed = date !== session.date || time !== session.time;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="move-session-modal">
      <div className="w-full max-w-md rounded-2xl bg-neutral-950 border border-neutral-800 p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-white font-semibold flex items-center gap-2"><Clock size={16} /> Move {session.name}'s session</h3>
            <p className="text-xs text-neutral-400 mt-1" data-testid="move-session-current">Now: {session.date} at {session.time} · {session.engineer_name}</p>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-white" data-testid="move-session-close"><X size={18} /></button>
        </div>
        <div className="flex gap-2">
          {[[30, '+30 min'], [60, '+1 hr'], [120, '+2 hr']].map(([m, l]) => (
            <button key={m} onClick={() => shift(m)} data-testid={`move-session-plus-${m}`}
              className="flex-1 py-2 rounded-full border border-purple-500/60 text-purple-300 text-sm font-semibold hover:bg-purple-500/10 transition-colors duration-200">{l}</button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="move-session-date"
            className="bg-black border border-neutral-700 rounded-lg px-3 py-2 text-sm text-white" />
          <select value={time} onChange={(e) => setTime(e.target.value)} data-testid="move-session-time"
            className="bg-black border border-neutral-700 rounded-lg px-3 py-2 text-sm text-white">
            {!TIMES.includes(time) && <option value={time}>{time}</option>}
            {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <p className="text-xs text-neutral-500">The client gets a text + email with the new time, the engineer gets a text, and Google Calendar updates.</p>
        <button onClick={save} disabled={!changed || saving} data-testid="move-session-save"
          className="w-full py-2.5 rounded-full bg-purple-600 hover:bg-purple-500 text-white font-semibold disabled:opacity-40 transition-colors duration-200">
          {saving ? 'Moving…' : changed ? `Move to ${date} · ${time}` : 'Pick a new time'}
        </button>
      </div>
    </div>
  );
};
